<template>
  <div class="page-scroll-container" ref="pageContainer">
    <!-- 1. [底层] 全屏模糊背景图 (永远铺满) -->
    <img
      class="full-screen-bg"
      src="../../../assets/annualReport/page3_blur.png"
      alt="bg-blur"
    />

    <!-- 2. [顶层] 光斑扩散层 (Intro Layer) 
         注意：放在 visual-card 外面，确保它是全屏的，且不受 card 透明度影响。
    -->
    <div class="intro-layer" :class="{ 'trigger-anim': hasIntroPlayed }">
      <div class="expanding-light"></div>
    </div>

    <!-- 3. [中间] 核心视觉卡片 -->
    <div
      class="visual-card"
      :class="{
        'text-playing': showTextAnim,
      }"
    >
      <!-- 3.1 卡片内部清晰背景图 -->
      <img
        class="card-bg"
        src="../../../assets/annualReport/page3_table_bag.png"
        alt="card-bg"
      />

      <!-- 3.2 内容区域 (保持不变) -->
      <div class="content">
        <div class="title anim-step-1">
          <div>尊敬的库迪主理人</div>
          <div class="nick-name">
            <div class="name">{{ reportData.memberNickName }}</div>
          </div>
        </div>

        <div class="item-row center-part">
          <div class="unlocked-date anim-step-2">
            <span class="dynamic">{{ year }}</span
            >年<span class="dynamic">{{ month }}</span
            >月<span class="dynamic">{{ day }}</span
            >日
          </div>

          <div class="text-group anim-step-3">
            <div>您解锁了第一杯</div>
            <div>Cotti特调</div>
            <div>就此开启与库迪咖啡</div>
            <div>互相成就的美好旅途</div>
          </div>
        </div>

        <div class="item-bottom-row anim-step-4">
          <div class="peer-date">今天，是我们携手</div>
          <div class="day-count">
            同行的第<span class="dynamic">{{ reportData.createDay }}</span
            >天
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import bgmSrc from "../../../assets/annualReport/coffeeWindChimes.mp3";

export default {
  name: "Page3Order",
  props: {
    reportData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      hasIntroPlayed: false,
      showTextAnim: false,
      audioInstance: null,
      observer: null,
    };
  },
  computed: {
    year() {
      const match = this.reportData.userCreateTime.match(/(\d+)年/);
      return match ? match[1] : "";
    },
    month() {
      const match = this.reportData.userCreateTime.match(/年(\d+)月/);
      return match ? match[1] : "";
    },
    day() {
      const match = this.reportData.userCreateTime.match(/月(\d+)日/);
      return match ? match[1] : "";
    },
  },
  mounted() {
    document.body.classList.add("lock-scroll");
    this.initAudio();
    this.initObserver();
  },
  beforeDestroy() {
    document.body.classList.remove("lock-scroll");
    this.cleanup();
  },
  methods: {
    initAudio() {
      this.audioInstance = new Audio(bgmSrc);
      this.audioInstance.loop = false;
      this.audioInstance.volume = 0.5;
      this.audioInstance.load();
      this.audioInstance.addEventListener("ended", this.onAudioEnded);
    },

    onAudioEnded() {
      this.$emit("audioFinished");
    },

    initObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting) {
            this.playAudio();
            this.showTextAnim = true;
            if (!this.hasIntroPlayed) {
              this.hasIntroPlayed = true;
            }
          } else {
            this.stopAudio();
            this.showTextAnim = false;
          }
        },
        { threshold: 0.5 }
      );

      if (this.$refs.pageContainer) {
        this.observer.observe(this.$refs.pageContainer);
      }
    },

    playAudio() {
      if (!this.audioInstance) return;
      window.dispatchEvent(new Event("global-bgm-pause"));
      this.audioInstance.currentTime = 0;
      this.audioInstance.play().catch((e) => {
        console.log("Page3 自动播放被拦截", e);
      });
    },

    stopAudio() {
      if (this.audioInstance) {
        this.audioInstance.pause();
        this.audioInstance.currentTime = 0;
      }
      window.dispatchEvent(new Event("global-bgm-resume"));
    },

    cleanup() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.audioInstance) {
        this.audioInstance.pause();
        this.audioInstance = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* =========================================
   1. 容器与全屏背景
   ========================================= */
.page-scroll-container {
  width: 100vw;
  height: 100vh;
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #fceeb5; /* 兜底色，防止图片加载前的白屏 */
}

/* 全屏模糊底图 */
.full-screen-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover; /* 关键：铺满 */
  z-index: 0;
  pointer-events: none;
}

/* =========================================
   2. Intro Layer (光斑遮罩层)
   必须是绝对定位且全屏，盖住上面的模糊图
   ========================================= */
.intro-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;

  overflow: hidden;
  /* 关键：设置背景色，动画开始前挡住所有内容 */
  background-color: #fceeb5;

  z-index: 999; /* 最高层级 */
  display: block;
  pointer-events: none;
  will-change: opacity;
}

/* 动画触发：淡出消失 */
.intro-layer.trigger-anim {
  animation: layerDisappear 1s ease-in-out forwards;
  animation-delay: 1.2s;
}

.expanding-light {
  position: absolute;
  top: 50%;
  right: 25%;
  left: auto;
  width: 5vh;
  height: 5vh;
  border-radius: 50%;
  transform: translateY(-50%) scale(1);

  /* 光斑样式 */
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 0.6) 80%
  );
  filter: blur(10px);
  box-shadow: 0 0 20px 5px rgba(255, 255, 255, 0.6);
  mix-blend-mode: screen;
  will-change: transform, opacity;
}

/* 动画触发：光斑扩散 */
.intro-layer.trigger-anim .expanding-light {
  animation: lightExpand 1.6s ease-out forwards;
}

@keyframes layerDisappear {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    visibility: hidden;
  }
}

@keyframes lightExpand {
  0% {
    transform: translateY(-50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translateY(-50%) scale(30);
    opacity: 1;
  }
}

/* =========================================
   3. 视觉卡片 (自适应核心)
   ========================================= */
.visual-card {
  position: relative;
  z-index: 1; /* 比背景高，比遮罩低 */
  width: 100vw;
  height: 100vh;
  container-type: size;

  /* 默认隐藏，等待动画 */
  opacity: 1;

  --unit: 1vw;
}

/* 卡片内部清晰背景图 */
.card-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: fill; /* 关键：填满卡片容器 */
  z-index: -1;
  pointer-events: none;
}

/* --- 屏幕适配逻辑 (13/25) --- */
@media (min-aspect-ratio: 13/25) {
  .visual-card {
    height: 100vh;
    width: calc(100vh * 390 / 844);
    aspect-ratio: 390 / 844;
    --unit: 0.462vh;

    /* 大屏时加个阴影更好看 */
    box-shadow: 0 0 30px rgba(0, 0, 0, 0.1);
  }
}
@media (max-aspect-ratio: 9/21) {
  .visual-card {
    width: 100vw;
    height: calc(100vw * 844 / 390);
    aspect-ratio: 390 / 844;
    --unit: 1vw;
  }
}

/* =========================================
   4. 内容与文字动画 (保持原样)
   ========================================= */
.content {
  position: absolute;
  top: 29.5%;
  left: 17.5%;
  height: auto;
  justify-content: center;
  gap: calc(5 * var(--unit));
  display: flex;
  flex-direction: column;
  font-size: calc(4.3 * var(--unit));
  color: #7a3e11;
  white-space: nowrap;
  font-weight: 600;
  z-index: 10;

  .title {
    display: flex;
    flex-direction: column;
    gap: calc(1.9 * var(--unit));
    .name {
      max-width: calc(45 * var(--unit));
      display: inline-block;
      vertical-align: bottom;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      font-size: calc(4.9 * var(--unit));
      color: #df6900;
      font-family: "MontserratRoman-Medium";
    }
  }
}

.item-row {
  display: flex;
  flex-direction: column;
  gap: calc(1.9 * var(--unit));
}
.item-bottom-row {
  display: flex;
  flex-direction: column;
  gap: calc(1 * var(--unit));
  .dynamic {
    position: relative;
    top: calc(0.3 * var(--unit));
  }
  .day-count {
    display: flex;
    align-items: baseline; /* 让文字底部基线对齐 */
  }
}
.dynamic {
  color: #df6900;
  font-size: calc(6.6 * var(--unit));
  font-family: "MontserratRoman-SemiBold";
  font-weight: 600;
}

/* 动画执行 */
.anim-step-1,
.anim-step-2,
.anim-step-3,
.anim-step-4 {
  opacity: 0;
  will-change: transform, opacity;
}

/* 卡片入场 */
.text-playing {
  // animation: cardEntrance 1.2s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.text-playing .anim-step-1 {
  animation: textFloatUp 1.2s ease-out forwards 1.5s;
}
.text-playing .anim-step-2 {
  animation: textFloatUp 1.2s ease-out forwards 2s;
}
.text-playing .anim-step-3 {
  animation: textFloatUp 1.2s ease-out forwards 2.5s;
  display: flex;
  flex-direction: column;
  gap: calc(1.9 * var(--unit));
}
.text-playing .anim-step-4 {
  animation: textFloatUp 1.2s ease-out forwards 3s;
}

@keyframes textFloatUp {
  from {
    opacity: 0;
    transform: translate3d(0, 30px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

.unlocked-date {
  display: flex;
  align-items: baseline;
  color: #7a3e11;
  .dynamic {
    position: relative;
    top: calc(0.2 * var(--unit));
  }
}
</style>
